import Header from '../Header/Header';
import { Icon, CustomTable, ConfirmationPopup, SuccessModal, Pagination, Search, Loader } from '@ohif/ui';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '@custom/Utils/config/apiRequest';
import { _machineUrls } from '@custom/Utils/constants/apiUrls';
import { customStyle } from '@custom/Utils/utils';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@custom/store/store';
import { getMachineListApi } from '@custom/rtk-apis/machine/getMachineList';
import { deleteMachineApi } from '@custom/rtk-apis/machine/deleteMachine';
import { getDeviceName } from '@custom/rtk-apis/machine/getDevice';
import { useTheme } from "../../ui/src/contextProviders";

const Machines = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { machinePayload, loading, success } = useSelector((state: RootState) => state.getMachineListApiSliceResponse);
  const { loading: deleteLoading, success: deleteSuccess } = useSelector((state: RootState) => state.deleteSelectedMachineSliceResponse);
  const navigate = useNavigate();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [tableData, setTableData] = useState([]);
  const [search, setSearch] = useState('');
  const [pgNumber, setPgNumber] = useState(1);
  const [pgLimit, setPgLimit] = useState(30);
  const [totalPg, setTotalPg] = useState(1000000);
  const [load, setLoad] = useState(true);
  const isDarkMode = useTheme();
  const [searchInputValue, setSearchInputValue] = useState('');

  const columns = [
    { key: 'machineName', title: 'Machine Name', sort: true },
    { key: 'aeTitle', title: 'AE Title' },
    { key: 'ipAddress', title: 'IP Address' },
    { key: 'port', title: 'Port' },
    { key: 'actions', title: 'Action' }
  ];

  const actions = [
    {
      label: 'Edit',
      iconName: 'edit',
      onClick: (item) => {
        navigate("/addmachines", { state: { machinesData: item , editMode : true} });
      }
    },
    {
      label: 'Delete',
      iconName: 'delete',
      onClick: (item) => {
        setSelectedRow(item);
        setIsPopupOpen(true);
      }
    },
  ];

  const handleConfirm = async () => {
    try {
      setIsPopupOpen(false);
      const payload = { deviceName: selectedRow?.machineName };
      await dispatch(deleteMachineApi.deleteSelectedMachineSlice(payload));
      dispatch(getMachineListApi.getAllMachineListApi({ search, limit: pgLimit, pageNumber: pgNumber }));
      setIsSuccessModal(true);
    } catch (error) {
      console.log(error);
    }
  };

  const onPageChangeHandler = (pg) => {
    setPgNumber(pg);
  };

  const handlePageSizeChange = (e) => {
    setPgLimit(e);
  };

  const searchClick = (searchText) => {
    const trimmedSearch = searchText.trim();
    setSearch(trimmedSearch);
    setSearchInputValue(searchText);
    setPgNumber(1);
  };

  useEffect(() => {
    const params = {
      limit: pgLimit,
      pageNumber: pgNumber,
      ...(search.trim() && { search })
    };

    dispatch(getMachineListApi.getAllMachineListApi(params));
  }, [dispatch, search, pgLimit, pgNumber]);

  useEffect(() => {
    if (success && machinePayload) {
      const mappedData = machinePayload?.map((item) => ({
        machineName: item?.dicomDeviceName
        ? item.dicomDeviceName
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ')
        : '--',
        dicomPrimaryDeviceType: '',
        aeTitle: item?.dicomAETitle || '--',
        ipAddress: item?.dicomNetworkConnection?.[0]?.dicomHostname || '--',
        port: item?.dicomNetworkConnection?.[0]?.dicomPort || '--',
      }));

      setTableData(mappedData || []);
      setTotalPg(machinePayload?.length || 0);
      setLoad(false);
    }
  }, [success, machinePayload]);

  return (
    <>
      <div className='page page__fullBody' style={
        isDarkMode?.isDarkMode ? { background: '#090c29' } : customStyle.mainBackground
      }>
        <Header isBack={false} title='Machines' />
        <div className='page__body'>
          <div className='tableWrap'>
            <div className='tableWrap__head'>
              <div className="formRow formRow__2 m_0 w_100 formRow--flexEnd">
                <div className='formCol'>
                  <Search text="Search by Machine Name" onSearchClick={searchClick} value={searchInputValue} setValue={setSearchInputValue} />
                </div>
              </div>
              <div className='btn__group'>
                <button className="btn btn__white" onClick={() => navigate("/addmachines")}>
                  <svg width="15" height="14" viewBox="0 0 15 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M7.63509 1.16699V12.8337M1.80176 7.00033H13.4684" stroke="#150823" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Add Machines
                </button>
              </div>
            </div>
            <div className='tableContainer'>
              <CustomTable columns={columns} data={tableData} actions={actions} searchQuery={searchQuery} from='machines' userDetails={''} setClickedItem={''} clickedItem={''} />
            </div>
          </div>
        </div>
        <Loader isLoaderOpen={load || loading || deleteLoading} />
        {tableData.length > 0 && (
          <Pagination
            onChangePage={onPageChangeHandler}
            onChangePerPage={handlePageSizeChange}
            totalPage={totalPg}
            currentPage={pgNumber}
            perPage={pgLimit}
          />
        )}
        <ConfirmationPopup
          title={''}
          isOpen={isPopupOpen}
          message={`Are you sure you want to delete ${selectedRow?.machineName}?`}
          onConfirm={handleConfirm}
          onCancel={() => setIsPopupOpen(false)}
        />
        <SuccessModal
          isSuccessModalOpen={isSuccessModalOpen}
          title={`Machine deleted successfully.`}
          handleCloseModal={() => setIsSuccessModal(false)}
        />
      </div>
    </>
  );
};

export default Machines;
